import React, { useState } from 'react';
import API_BASE_URL from '../../config';
import './PlaylistChecker.css';

const PlaylistChecker = () => {
  const [url, setUrl] = useState('');
  const [m3uData, setM3uData] = useState(null);
  const [results, setResults] = useState({ online: [], offline: [] });
  const [loading, setLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false); // Track if checking is fully done
  
  const [progress, setProgress] = useState({
    current: 0,
    total: 0,
    onlineCount: 0,
    offlineCount: 0
  });

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target.result;
      setM3uData(content);
      setUrl(file.name);
    };
    reader.readAsText(file);
  };

  const handleCheck = async () => {
    if (!url && !m3uData) {
      alert("Please enter a URL or import a file!");
      return;
    }

    setLoading(true);
    setIsCompleted(false); // Reset completion state
    setResults({ online: [], offline: [] });
    setProgress({ current: 0, total: 0, onlineCount: 0, offlineCount: 0 });

    try {
      const payload = m3uData
        ? { m3u_content: m3uData }
        : { url: url.trim() };

      const response = await fetch(`${API_BASE_URL}/diagnostic-tools/playlist-check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Server returned status ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const rawData = line.replace('data: ', '').trim();
            if (!rawData) continue;

            try {
              const parsed = JSON.parse(rawData);

              if (parsed.status === 'completed') {
                setLoading(false);
                setIsCompleted(true); // Mark as fully complete
                break;
              }

              if (parsed.total && !parsed.latest) {
                setProgress(prev => ({ ...prev, total: parsed.total }));
              }

              if (parsed.status === 'progress') {
                setProgress({
                  current: parsed.current,
                  total: parsed.total,
                  onlineCount: parsed.online_count,
                  offlineCount: parsed.offline_count
                });

                const newChannel = parsed.latest.channel;
                if (parsed.latest.status === 'online') {
                  setResults(prev => ({
                    ...prev,
                    online: [...prev.online, newChannel]
                  }));
                } else {
                  setResults(prev => ({
                    ...prev,
                    offline: [...prev.offline, newChannel]
                  }));
                }
              }
            } catch (e) {
              console.error("Error parsing stream line:", e);
            }
          }
        }
      }

    } catch (error) {
      console.error(error);
      alert("Failed to check playlist. Stream disconnected.");
      setLoading(false);
    }
  };

  // 📥 Function to convert JSON back to a standard M3U8 file and download it
  const exportToM3U = (channelList, filename) => {
    if (!channelList || channelList.length === 0) {
      alert("No data available to export!");
      return;
    }

    // Build the M3U string with standard headers
    let m3uContent = "#EXTM3U\n";
    
    channelList.forEach(ch => {
      const logoAttr = ch.logo ? ` tvg-logo="${ch.logo}"` : "";
      m3uContent += `#EXTINF:-1${logoAttr},${ch.name}\n${ch.url}\n`;
    });

    // Create a Blob and trigger an automatic browser download
    const blob = new Blob([m3uContent], { type: 'text/plain;charset=utf-8' });
    const element = document.createElement("a");
    element.href = URL.createObjectURL(blob);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element); // Clean up the DOM
  };

  const copyToClipboard = (link) => {
    navigator.clipboard.writeText(link);
    alert("URL Copied!");
  };

  return (
    <div className="main-wrapper">
      <div className="checker-card">
        <h3 className="header-main-title">M3U8 Playlist Checker</h3>

        <div className="input-section mb-4">
          <div style={{ flex: 2 }}>
            <label className="input-label">Input URL:</label>
            <input
              className="url-input"
              type="text"
              placeholder="http://example.com/playlist.m3u"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setM3uData(null);
              }}
            />
          </div>

          <div className="file-input-wrapper">
            <label className="input-label">Import M3U/Text File:</label>
            <input
              type="file"
              className="file-input"
              accept=".m3u,.m3u8,.txt"
              onChange={handleFileUpload}
            />
          </div>

          <button
            className="check-btn"
            onClick={handleCheck}
            disabled={loading}
          >
            {loading ? 'Analyzing...' : 'Submit'}
          </button>
        </div>

        {/* Live Progress Section */}
        {(loading || progress.current > 0) && (
          <div className="progress-section mb-4" style={{ padding: '15px', background: '#f5f5f5', borderRadius: '8px' }}>
            <h5>Progress: {progress.current} / {progress.total} Channels Checked</h5>
            <div style={{ background: '#ddd', height: '10px', borderRadius: '5px', overflow: 'hidden', marginTop: '10px' }}>
              <div style={{ 
                background: '#4CAF50', 
                height: '100%', 
                width: `${(progress.current / (progress.total || 1)) * 100}%`,
                transition: 'width 0.2s ease'
              }}></div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10px', fontSize: '14px' }}>
              <span style={{ color: 'green' }}>🟢 Online: {progress.onlineCount}</span>
              <span style={{ color: 'red' }}>🔴 Offline: {progress.offlineCount}</span>
            </div>
          </div>
        )}

        {/* Results Columns */}
        {(results.online.length > 0 || results.offline.length > 0) && (
          <div className="results-container">
            
            {/* Online Column */}
            <div className="status-column-wrapper">
              <div className="status-column">
                <div className="header-title online-title">
                  Online Item [{results.online.length}]
                </div>
                {results.online.map((ch, i) => (
                  <div key={i} className="stream-item">
                    <div className="stream-info">
                      <span className="stream-name">{ch.name}</span>
                      <span className="stream-url">{ch.url}</span>
                    </div>
                    <button className="copy-button" onClick={() => copyToClipboard(ch.url)}>Copy</button>
                  </div>
                ))}
              </div>
              {/* Export Button for Online Items */}
              {isCompleted && results.online.length > 0 && (
                <button 
                  className="export-btn online-export-btn"
                  onClick={() => exportToM3U(results.online, "online_playlist.m3u8")}
                >
                  📥 Export Online List
                </button>
              )}
            </div>

            {/* Offline Column */}
            <div className="status-column-wrapper">
              <div className="status-column">
                <div className="header-title offline-title">
                  Offline Item [{results.offline.length}]
                </div>
                {results.offline.map((ch, i) => (
                  <div key={i} className="stream-item">
                    <div className="stream-info">
                      <span className="stream-name">{ch.name}</span>
                      <span className="stream-url">{ch.url}</span>
                    </div>
                    <span className="offline-badge">Offline</span>
                  </div>
                ))}
              </div>
              {/* Export Button for Offline Items */}
              {isCompleted && results.offline.length > 0 && (
                <button 
                  className="export-btn offline-export-btn"
                  onClick={() => exportToM3U(results.offline, "offline_playlist.m3u8")}
                >
                  📥 Export Offline List
                </button>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default PlaylistChecker;